import { TimerAction, TimestampEntity } from '../timer/state/timer.interface';
import { calculateSessionTime } from './timestamp.utils';

export interface RemoteDayDoc {
  date: string; // YYYY-MM-DD
  items: TimestampEntity[];
  metadata: { goal: number | null; multiplier: number };
  revision?: number; // missing (v1/old-client doc) => 0
  modifiedAt?: number; // epoch ms; missing => 0
  writerId?: string; // per-install device id; missing => ''
}

export interface LocalDayState {
  date: string;
  items: TimestampEntity[];
  goal: number | null;
  multiplier: number;
  baseRevision: number; // 0 = never synced
  dirty: boolean;
  modifiedAt?: number; // epoch ms of last local change
  writerId?: string; // this device's id
}

export type DayResolution =
  | { action: 'none' }
  | { action: 'adopt' } // replace local day with remote; engine sets baseRevision = remote revision, dirty = false
  | { action: 'write'; revision: number } // upload local day with this revision
  | { action: 'fork-local-wins'; revision: number } // arbitrated fork: upload local with this revision; engine stashes the losing remote version
  | { action: 'fork-remote-wins' }; // arbitrated fork: adopt remote; engine stashes the losing local version

/**
 * Decides what a device should do with a single day's log given its local state and the
 * current remote (Firestore) document for that day. Pure decision core -- callers own actually
 * performing the write/adopt/fork side effects.
 */
export function resolveDay(
  local: LocalDayState | null,
  remote: RemoteDayDoc | null,
): DayResolution {
  if (!remote) {
    if (!local || local.items.length === 0) {
      return { action: 'none' };
    }
    return { action: 'write', revision: local.baseRevision + 1 };
  }

  const remoteRev = remote.revision ?? 0;

  if (!local || (local.items.length === 0 && !local.dirty)) {
    return { action: 'adopt' };
  }

  if (!local.dirty) {
    if (remoteRev !== local.baseRevision) {
      return { action: 'adopt' };
    }

    // Same revision but the content diverges. For a clean local this can only happen when two
    // devices wrote the same revision concurrently, or when an in-flight save's markSynced cleared
    // the dirty flag over a newer local edit. Arbitrate as a fork so the divergence is healed
    // instead of silently kept. An echo of our own write has identical items => still 'none', so no
    // write loops. (Covers the old rev-0/0 first-contact case as a subset.)
    if (!itemsEqual(local.items, remote.items)) {
      return arbitrateFork(local, remote, remoteRev);
    }

    return { action: 'none' };
  }

  if (remoteRev === local.baseRevision) {
    return { action: 'write', revision: local.baseRevision + 1 };
  }

  return arbitrateFork(local, remote, remoteRev);
}

function arbitrateFork(
  local: LocalDayState,
  remote: RemoteDayDoc,
  remoteRev: number,
): DayResolution {
  if (pickDayWinner(local, remote) === 'local') {
    return { action: 'fork-local-wins', revision: Math.max(remoteRev, local.baseRevision) + 1 };
  }
  return { action: 'fork-remote-wins' };
}

/**
 * Picks which of two conflicting versions of the same day should win an arbitrated fork.
 * The day with the longest focus time wins; ties cascade through rest time, entry count,
 * last-modified time, and finally writer id, before defaulting to the remote (persisted) side.
 *
 * These calculateSessionTime calls deliberately omit dayStartHour (leaving it on
 * DEFAULT_DAY_START_HOUR) rather than reading the user's configured hour. Both sides being
 * compared are the SAME day, so a shared constant is symmetric and correct here regardless of
 * what it is — and this module must stay pure with no settings/DI dependency. Do not "fix" this
 * to read SettingsRepository.
 */
export function pickDayWinner(local: LocalDayState, remote: RemoteDayDoc): 'local' | 'remote' {
  const byFocus = compare(
    calculateSessionTime(TimerAction.Focus, local.items),
    calculateSessionTime(TimerAction.Focus, remote.items),
  );
  if (byFocus) {
    return byFocus;
  }

  const byRest = compare(
    calculateSessionTime(TimerAction.Rest, local.items),
    calculateSessionTime(TimerAction.Rest, remote.items),
  );
  if (byRest) {
    return byRest;
  }

  const byCount = compare(local.items.length, remote.items.length);
  if (byCount) {
    return byCount;
  }

  const byModifiedAt = compare(local.modifiedAt ?? 0, remote.modifiedAt ?? 0);
  if (byModifiedAt) {
    return byModifiedAt;
  }

  const byWriterId = compare(local.writerId ?? '', remote.writerId ?? '');
  if (byWriterId) {
    return byWriterId;
  }

  return 'remote';
}

/** Returns which side is greater, or `null` if they are equal. */
function compare<T>(localValue: T, remoteValue: T): 'local' | 'remote' | null {
  if (localValue > remoteValue) {
    return 'local';
  }
  if (remoteValue > localValue) {
    return 'remote';
  }
  return null;
}

/** Order-sensitive structural equality of two entry logs (id + timestamp + action). */
export function itemsEqual(a: TimestampEntity[], b: TimestampEntity[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every(
    (item, index) =>
      item.id === b[index].id &&
      item.timestamp === b[index].timestamp &&
      item.action === b[index].action,
  );
}
